import { BASE_INFO_OPTION_META_KEYS } from '../../../capabilities/base-info/fieldKeys';
import { NETWORK_OPTION_META_KEYS } from '../../../capabilities/network/fieldKeys';
import { serializeServiceListForApi } from '../../../capabilities/service/serviceApiField';
import type { DataDiskRecord } from '../../../capabilities/storage/types';
import type { PipelineContext } from '../../../core/types';
import type { DisplayDescriptor } from '../../../platform/display-descriptors';
import { resolveOptionDisplayValue } from '../../../platform/option-meta';
import type { ResourceType } from '../../../platform/pipeline-kernel/types';
import { resolvePricingRequirement, stripEmptyObjects } from '../../../utils';
import { pickPricingResourceMeta } from '../../../utils/pricingResourceMeta';
import { toVendorDeviceDeliveryFields } from '../../../utils/vendorDeviceSubmit';
import { vmBandwidth, vmCpu, vmDataStorage, vmGpu, vmMemory, vmSystemStorage } from '../resourceConfigs';
import { filterVmSpecByEnabledModules, isVmModuleEnabled } from './enabledModules';
import { hasConfiguredGpu } from './gpu';
import type { VmApiPayload, VmSpec } from './schema';
import { isVmMemoryReady, readVmCpuTotal } from './spec';
import { mergeVmSystem, serializeDataDiskVolumes, serializeSharedVolumes, serializeSystemDiskVolume } from './volumes';

const PREVIEW_EMPTY = undefined;

function previewValue(value: unknown): string {
  if (value === undefined || value === null) return PREVIEW_EMPTY;
  if (typeof value === 'string' && !value.trim()) return PREVIEW_EMPTY;
  return String(value);
}

function previewRow(id: string, group: string, label: string, value: unknown, tag?: string): DisplayDescriptor {
  return { id, group, label, value: previewValue(value), tag };
}

function formatCpuPreview(cpu: VmSpec['resource']['cpu'], cpuTotal: number | null): string | undefined {
  if (cpuTotal == null) return PREVIEW_EMPTY;
  const { physicalCards, physicalCores, logicalCores } = cpu;
  return `${physicalCards} × ${physicalCores} × ${logicalCores} = ${cpuTotal} 核`;
}

function filterConfirmDescriptors(items: DisplayDescriptor[]): DisplayDescriptor[] {
  const result: DisplayDescriptor[] = [];
  for (const item of items) {
    const children = item.children?.length ? filterConfirmDescriptors(item.children) : undefined;
    const value = String(item.value ?? '').trim();
    if (!children?.length && (!value || value === PREVIEW_EMPTY)) continue;
    result.push(children?.length ? { ...item, children } : item);
  }
  return result;
}

function previewList(values: string[] | undefined, separator = '、'): string {
  if (!values?.length) return PREVIEW_EMPTY;
  const items = values.map((item) => String(item).trim()).filter(Boolean);
  return items.length ? items.join(separator) : PREVIEW_EMPTY;
}

function resolveSystemImageName(image?: { name?: string; os?: string; version?: string }): string {
  const name = image?.name?.trim();
  if (name) return name;
  const os = image?.os?.trim();
  const version = image?.version?.trim();
  if (os && version) return `${os} ${version}`;
  return os || version || '';
}

function formatSystemImage(spec: VmSpec): string {
  const imageName = resolveSystemImageName(spec.systemDisk?.image);
  if (imageName) return imageName;
  const systemOs = spec.system?.os?.trim();
  return systemOs || PREVIEW_EMPTY;
}

function formatSystemDisk(spec: VmSpec, optionMetaForm: Record<string, unknown>): string | undefined {
  const disk = spec.systemDisk;
  if (!disk?.diskSize) return PREVIEW_EMPTY;
  const storageClass = resolveOptionDisplayValue(optionMetaForm, 'systemDisk.storageClass', disk.storageClass) ?? disk.storageClass;
  const size = `${disk.diskSize} Gi`;
  const storageClassLabel = String(storageClass ?? '').trim();
  return storageClassLabel ? `${storageClassLabel} * ${size}` : size;
}

function formatGpu(spec: VmSpec, optionMetaForm: Record<string, unknown>): string {
  const gpu = spec.resource.gpu;
  if (!hasConfiguredGpu(gpu)) return PREVIEW_EMPTY;
  const vendorDeviceLabel = resolveOptionDisplayValue(optionMetaForm, 'resource.gpu', gpu);
  const label = vendorDeviceLabel || [gpu?.name, gpu?.vendor, gpu?.device].filter(Boolean).join(' ');
  const qty = gpu?.number ?? 1;
  const qtyWithUnit = `${qty} ${vmGpu.unit}`;
  return label ? `${label} × ${qtyWithUnit}` : qtyWithUnit;
}

function formatDataDiskValue(disk: DataDiskRecord, index: number, optionMetaForm: Record<string, unknown>): string | undefined {
  if (!disk.diskSize) return PREVIEW_EMPTY;
  const storageClass = resolveOptionDisplayValue(optionMetaForm, `dataDisks.${index}.storageClass`, disk.storageClass) ?? disk.storageClass;
  const size = `${disk.diskSize} Gi`;
  const storageClassLabel = String(storageClass ?? '').trim();
  return storageClassLabel ? `${storageClassLabel} * ${size}` : size;
}

function buildDataDiskSections(disks: DataDiskRecord[], optionMetaForm: Record<string, unknown>): DisplayDescriptor[] {
  if (!disks.length) {
    return [previewRow('dataDisk-empty', 'dataDisk', '数据盘', PREVIEW_EMPTY)];
  }

  return disks.map((disk, index) =>
    previewRow(
      `dataDisk-${index}`,
      'dataDisk',
      disk.diskNickname || disk.diskName,
      formatDataDiskValue(disk, index, optionMetaForm),
      '数据盘',
    ),
  );
}

export function serializeVmApiBody(spec: VmSpec, ctx: PipelineContext): VmApiPayload {
  const cpu = spec.resource.cpu;
  if (!isVmMemoryReady(spec.resource.memory) || readVmCpuTotal(cpu) == null) {
    throw new Error('云主机 CPU 与内存为必填项');
  }

  const creator = ctx.overlay?.creator ?? '';
  const { volumes: systemVolumes, system: systemFromDisk, image, source } = serializeSystemDiskVolume(spec.systemDisk, creator);
  const dataVolumes = serializeDataDiskVolumes(spec.dataDisks, creator);
  const sharedVolumes = serializeSharedVolumes(spec.volumes);

  const payload: VmApiPayload = {
    name: spec.name,
    description: spec.description,
    runStrategy: spec.runStrategy,
    creator,
    image,
    source,
    system: mergeVmSystem(systemFromDisk, spec.system),
    resource: {
      memory: `${spec.resource.memory}Gi`,
      cpu: {
        physicalCards: spec.resource.cpu.physicalCards,
        physicalCores: spec.resource.cpu.physicalCores,
        logicalCores: spec.resource.cpu.logicalCores,
      },
      network: {
        vpc: spec.resource.network.vpc,
        subnet: spec.resource.network.subnet,
        securitygroup: spec.resource.network.securitygroup,
        dnsConfig: spec.resource.network.dnsConfig,
        hpc: spec.resource.network.hpc,
        service: serializeServiceListForApi(spec.resource.network.service),
      },
      volumes: [...systemVolumes, ...dataVolumes, ...sharedVolumes],
    },
  };

  if (spec.workspace) {
    payload.workspaces = [spec.workspace];
  }

  const gpu = spec.resource.gpu;
  if (hasConfiguredGpu(gpu)) {
    (payload.resource as Record<string, unknown>).gpu = {
      ...toVendorDeviceDeliveryFields(gpu),
      name: gpu?.name,
      number: gpu?.number ?? 1,
    };
  }

  if (spec.resource.network.Bandwidth !== undefined && spec.resource.network.Bandwidth !== '') {
    const resourceBlock = payload.resource as Record<string, unknown>;
    const networkBlock = (resourceBlock.network ?? {}) as Record<string, unknown>;
    resourceBlock.network = {
      ...networkBlock,
      Bandwidth: String(spec.resource.network.Bandwidth),
    };
  }

  return stripEmptyObjects(payload);
}

export function pickVmPresetFields(spec: VmSpec, ctx: PipelineContext): unknown {
  const enabledModules = ctx.sku?.enabledModules;
  const skuType = ctx.sku?.skuType;
  const filtered = filterVmSpecByEnabledModules(spec, enabledModules, skuType);
  return {
    name: filtered.name,
    description: filtered.description,
    runStrategy: filtered.runStrategy,
    workspace: filtered.workspace,
    system: filtered.system,
    systemDisk: filtered.systemDisk,
    dataDisks: filtered.dataDisks,
    volumes: filtered.volumes,
    resource: filtered.resource,
    ...(filtered.optionMeta ? { optionMeta: filtered.optionMeta } : {}),
    ...(enabledModules ? { skuResourceModules: enabledModules } : {}),
  };
}

export interface BuildVmDisplayOptions {
  displayMode?: 'productCard' | 'confirm';
}

export function buildVmDisplayDescriptors(spec: VmSpec, ctx: PipelineContext, options: BuildVmDisplayOptions = {}): DisplayDescriptor[] {
  const enabledModules = ctx.sku?.enabledModules;
  const skuType = ctx.sku?.skuType;
  const filtered = filterVmSpecByEnabledModules(spec, enabledModules, skuType);
  const optionMetaForm = spec.optionMeta ? { optionMeta: spec.optionMeta } : {};
  const cpu = filtered.resource.cpu;
  const cpuTotal = readVmCpuTotal(cpu);
  const network = filtered.resource.network;
  const dns = network.dnsConfig;
  const dataDisks = filtered.dataDisks ?? [];

  const baseInfo: DisplayDescriptor[] = isVmModuleEnabled('baseInfo', enabledModules, skuType)
    ? [
        previewRow('name', 'baseInfo', '名称', filtered.name),
        previewRow('description', 'baseInfo', '描述', filtered.description),
        previewRow(
          'workspace',
          'baseInfo',
          '租户空间',
          resolveOptionDisplayValue(optionMetaForm, 'workspace', filtered.workspace, BASE_INFO_OPTION_META_KEYS.workspace),
        ),
        previewRow('namespace', 'baseInfo', '命名空间', ctx.namespace),
        previewRow('datacenter', 'baseInfo', '数据中心', filtered.datacenter ?? ctx.cluster),
      ]
    : [];

  const vpc = resolveOptionDisplayValue(optionMetaForm, 'resource.network.vpc.name', network.vpc?.name, NETWORK_OPTION_META_KEYS.vpc);
  const networkItems: DisplayDescriptor[] = isVmModuleEnabled('network', enabledModules, skuType)
    ? [
        previewRow('vpc', 'network', 'VPC', vpc),
        previewRow(
          'subnet',
          'network',
          '子网',
          resolveOptionDisplayValue(optionMetaForm, 'resource.network.subnet.name', network.subnet?.name, NETWORK_OPTION_META_KEYS.subnet),
        ),
        previewRow(
          'securitygroup',
          'network',
          '安全组',
          resolveOptionDisplayValue(
            optionMetaForm,
            'resource.network.securitygroup.name',
            network.securitygroup?.name,
            NETWORK_OPTION_META_KEYS.securitygroup,
          ),
        ),
      ]
    : [];

  const systemSetting: DisplayDescriptor[] = isVmModuleEnabled('systemSetting', enabledModules, skuType)
    ? [
        previewRow('runStrategy', 'systemSetting', '运行策略', filtered.runStrategy),
        previewRow('systemImage', 'systemSetting', '操作系统', formatSystemImage(filtered)),
        previewRow('systemDisk', 'systemSetting', '系统盘大小', formatSystemDisk(filtered, optionMetaForm)),
      ]
    : [];

  const resourceItems: DisplayDescriptor[] = isVmModuleEnabled('resource', enabledModules, skuType)
    ? [
        previewRow('architecture', 'resource', '架构', filtered.system?.architecture),
        previewRow('cpuTotal', 'resource', 'CPU', formatCpuPreview(cpu, cpuTotal)),
        previewRow(
          'memory',
          'resource',
          '内存',
          isVmMemoryReady(filtered.resource.memory) ? `${filtered.resource.memory} Gi` : PREVIEW_EMPTY,
        ),
        previewRow('gpu', 'resource', 'GPU', formatGpu(filtered, optionMetaForm)),
        previewRow('bandwidth', 'resource', '网络带宽', network.Bandwidth),
      ]
    : [];

  const dataDiskItems = isVmModuleEnabled('dataDisk', enabledModules, skuType) ? buildDataDiskSections(dataDisks, optionMetaForm) : [];

  const dnsItems: DisplayDescriptor[] = isVmModuleEnabled('dns', enabledModules, skuType)
    ? [
        previewRow('dnsNameservers', 'dns', 'DNS 服务器', previewList(dns?.nameservers)),
        previewRow('dnsSearches', 'dns', 'DNS 搜索域', previewList(dns?.searches)),
        previewRow(
          'dnsOptions',
          'dns',
          'DNS 选项',
          dns?.options?.length
            ? dns.options
                .filter((item) => item.name?.trim())
                .map((item) => `${item.name}=${item.value ?? ''}`)
                .join('；') || PREVIEW_EMPTY
            : PREVIEW_EMPTY,
        ),
      ]
    : [];

  const descriptors = [...baseInfo, ...networkItems, ...systemSetting, ...resourceItems, ...dataDiskItems, ...dnsItems];
  return options.displayMode === 'confirm' ? filterConfirmDescriptors(descriptors) : descriptors;
}

export function buildVmResourceConfigs(spec: VmSpec, ctx: PipelineContext): ResourceType[] {
  const filtered = filterVmSpecByEnabledModules(spec, ctx.sku?.enabledModules, ctx.sku?.skuType);
  const configs: ResourceType[] = [];
  const paymentTiming = ctx.paymentTiming;
  const purpose = ctx.resourceQuantityPurpose;
  const skuType = ctx.sku?.skuType;
  const cpuTotal = readVmCpuTotal(filtered.resource.cpu);
  const cpuPricing = filtered.resource.cpu.pricing ?? filtered.resource.cpuPricing;
  const cpuRequirement = resolvePricingRequirement(cpuTotal, { paymentTiming, skuType, vendorMeta: cpuPricing, purpose });
  if (cpuRequirement != null) {
    configs.push({
      ...vmCpu,
      ...pickPricingResourceMeta(cpuPricing, cpuRequirement),
      requirement: cpuRequirement,
    });
  }

  const memoryPricing = filtered.resource.memoryPricing;
  const memoryRequirement = resolvePricingRequirement(isVmMemoryReady(filtered.resource.memory) ? filtered.resource.memory : undefined, {
    paymentTiming,
    skuType,
    vendorMeta: memoryPricing,
    purpose,
  });
  if (memoryRequirement != null) {
    configs.push({
      ...vmMemory,
      ...pickPricingResourceMeta(memoryPricing, memoryRequirement),
      requirement: memoryRequirement,
    });
  }

  const gpu = filtered.resource.gpu;
  const gpuPricing = filtered.resource.gpuPricing;
  /** GPU 厂商设备写在 resource.gpu（含 quantityUnit），gpuPricing 为兼容残留 */
  const gpuPricingMeta = {
    ...gpuPricing,
    vendor: gpu?.vendor ?? gpuPricing?.vendor,
    device: gpu?.device ?? gpuPricing?.device,
    vendorID: gpu?.vendorID ?? gpuPricing?.vendorID,
    deviceID: gpu?.deviceID ?? gpuPricing?.deviceID,
    quantityUnit:
      (gpu as { quantityUnit?: string } | undefined)?.quantityUnit ?? (gpuPricing as { quantityUnit?: string } | undefined)?.quantityUnit,
  };
  const gpuRequirement = resolvePricingRequirement(gpu?.number, { paymentTiming, skuType, vendorMeta: gpuPricingMeta, purpose });
  if (gpuRequirement != null) {
    configs.push({
      ...vmGpu,
      ...pickPricingResourceMeta(gpuPricingMeta, gpuRequirement),
      requirement: gpuRequirement,
    });
  }

  const bandwidthPricing = filtered.resource.network.bandwidthPricing;
  const bandwidthRequirement = resolvePricingRequirement(filtered.resource.network.Bandwidth, {
    paymentTiming,
    skuType,
    vendorMeta: bandwidthPricing,
    purpose,
  });
  if (bandwidthRequirement != null) {
    configs.push({
      ...vmBandwidth,
      ...pickPricingResourceMeta(bandwidthPricing, bandwidthRequirement),
      requirement: bandwidthRequirement,
    });
  }

  if (filtered.systemDisk && filtered.systemDisk.sourceKind !== 'existing') {
    const systemDiskPricing = filtered.systemDisk.pricing;
    const systemDiskRequirement = resolvePricingRequirement(filtered.systemDisk.diskSize, {
      paymentTiming,
      skuType,
      vendorMeta: systemDiskPricing,
      purpose,
    });
    if (systemDiskRequirement != null) {
      configs.push({
        ...vmSystemStorage,
        ...pickPricingResourceMeta(systemDiskPricing, systemDiskRequirement),
        requirement: systemDiskRequirement,
      });
    }
  }

  let pricedDataDiskIndex = 0;
  filtered.dataDisks?.forEach((disk) => {
    if (disk.sourceKind === 'existing') return;
    const requirement = resolvePricingRequirement(disk.diskSize, { paymentTiming, skuType, vendorMeta: disk.pricing, purpose });
    if (requirement == null) return;
    configs.push({
      ...vmDataStorage,
      description: pricedDataDiskIndex > 0 ? `${vmDataStorage.description}${pricedDataDiskIndex + 1}` : vmDataStorage.description,
      ...pickPricingResourceMeta(disk.pricing, requirement),
      requirement,
    });
    pricedDataDiskIndex += 1;
  });

  return configs;
}
